import {
  GA_MEASUREMENT_ID,
  isAnalyticsConfigured,
} from "./analyticsConfig"

const GTAG_READY_EVENT = "gtagjs-loaded"
const GTAG_READY_TIMEOUT_MS = 4000

let gtagReady = false
let gtagReadyCallbacks: Array<() => void> = []
let gtagReadyTimeoutStarted = false
let lastTrackedKey = ""

const pageLocationFromPath = (path: string): string  => {
  try {
    return new URL(path, window.location.origin).href
  } catch {
    return window.location.href
  }
}

const markGtagReady = (): void  => {
  if (gtagReady) {
    return
  }

  gtagReady = true

  for (const callback of gtagReadyCallbacks) {
    callback()
  }

  gtagReadyCallbacks = []
}

const whenGtagReady = (callback: () => void): void => {
  if (gtagReady) {
    callback()
    return
  }

  gtagReadyCallbacks.push(callback)

  if (!gtagReadyTimeoutStarted) {
    gtagReadyTimeoutStarted = true
    window.setTimeout(() => {
      markGtagReady()
    }, GTAG_READY_TIMEOUT_MS)
  }
}

const ensureGtagStub = (): void  => {
  window.dataLayer = window.dataLayer ?? []

  if (typeof window.gtag !== "function") {
    window.gtag = (...args: unknown[]): void => {
      window.dataLayer?.push(args)
    }
  }
}

/** Fallback when gtag is not injected via gatsby-ssr (e.g. local develop). */
const loadAnalyticsScript = (): void  => {
  if (!isAnalyticsConfigured()) {
    return
  }

  ensureGtagStub()

  const existingScript = document.querySelector<HTMLScriptElement>(
    'script[src*="googletagmanager.com/gtag/js"]'
  )

  if (existingScript) {
    if (existingScript.dataset.gtagReady === "true") {
      markGtagReady()
      return
    }

    existingScript.addEventListener(
      "load",
      () => {
        existingScript.dataset.gtagReady = "true"
        markGtagReady()
      },
      { once: true }
    )
    return
  }

  window.gtag?.("js", new Date())
  window.gtag?.("config", GA_MEASUREMENT_ID, { send_page_view: false })

  const script = document.createElement("script")
  script.async = true
  script.dataset.cfasync = "false"
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  script.addEventListener(
    "load",
    () => {
      script.dataset.gtagReady = "true"
      markGtagReady()
    },
    { once: true }
  )
  document.head.appendChild(script)
}

const sendPageView = (path: string, title?: string): void  => {
  if (typeof window.gtag !== "function") {
    return
  }

  const pagePath =
    path ||
    window.location.pathname + window.location.search + window.location.hash

  const pageTitle = title ?? document.title
  const trackKey = `${pagePath}|${pageTitle}`

  if (trackKey === lastTrackedKey) {
    return
  }

  lastTrackedKey = trackKey

  window.gtag("event", "page_view", {
    send_to: GA_MEASUREMENT_ID,
    page_path: pagePath,
    page_title: pageTitle,
    page_location: pageLocationFromPath(pagePath),
  })
}

export const trackPageView = (path: string, title?: string): void  => {
  if (!isAnalyticsConfigured() || typeof window === "undefined") {
    return
  }

  ensureGtagStub()
  loadAnalyticsScript()

  whenGtagReady(() => {
    sendPageView(path, title)
  })
}

export const initPageviewAnalytics = (): void  => {
  if (!isAnalyticsConfigured() || typeof window === "undefined") {
    return
  }

  ensureGtagStub()

  window.addEventListener(GTAG_READY_EVENT, markGtagReady, { once: true })

  const script = document.querySelector<HTMLScriptElement>(
    'script[src*="googletagmanager.com/gtag/js"]'
  )

  if (script?.dataset.gtagReady === "true") {
    markGtagReady()
    return
  }

  if (script) {
    script.addEventListener(
      "load",
      () => {
        script.dataset.gtagReady = "true"
        markGtagReady()
      },
      { once: true }
    )
  }
}
